import getFilter from '../filterFactory';
import { getRelationMetadata } from '../metadata';
import lodash from 'lodash';
const { merge } = lodash;

const _p = { getRelationMetadata, getFilter };
export const _private = _p;
/**
 * Build the server URL for a set of backbone filters.
 * @param {string} namespace
 * @param {string} relation
 * @param {Record<string, any>} requestFilters backbone style filters, e.g. getFiltersFromView() output.
 * @param {string} [correlationId]
 * @param {object} [options]
 * @param {boolean} [options.includeEndpointSuffix] DEFAULT true. Set to false when the URL is not
 * a read of `namespace`/`relation` itself, so the `/aggregate` and `/search` endpoints are left off.
 * @returns {string}
 */
export default function getQueryUrl(namespace, relation, requestFilters, correlationId, options = {}) {
    const { includeEndpointSuffix = true } = options;
    let baseUrl = `/api/${namespace}/${relation}`;
    if (!requestFilters || Object.keys(requestFilters).length === 0) {
        return baseUrl;
    }
    // `/aggregate` and `/search` are read endpoints that only exist for a data relation. A caller
    // sending the filters somewhere else (report/generate, to describe what to export) turns them
    // off: the server has no such endpoint there, so it reads the extra path segment as a record
    // id, and the request then matches nothing.
    if (includeEndpointSuffix) {
        if (requestFilters.aggregateType) {
            baseUrl += '/aggregate';
        }
        if (requestFilters.fullTextSearch) {
            baseUrl += '/search';
        }
    }

    /**
     * @type {string[]}
     */
    let filterPieces = [];
    /**
     * @type {Record<string, any>}
     */
    let filterObject = {};
    const { limitSyncSize } = _p.getRelationMetadata(namespace, relation);

    Object.keys(requestFilters).forEach(filterName => {
        let filter = _p.getFilter(filterName);
        if (!filter) return; // if we don't have a filter, we can't add it to the url, so skip it.
        if (filter.getUriComponent) {
            let piece = filter.getUriComponent(requestFilters, limitSyncSize);
            if (typeof piece === 'string') {
                if (!piece || piece.length === 0) return; // skip empty parameters.
                filterPieces.push(piece);
            } else if (typeof piece === 'object') {
                if (Object.keys(piece).length === 0) return; // skip empty parameters.
                // the primary type of data being returned here is likely an object with a "criteria" property
                // but making this generic to make it future proof.
                const { criteria, ...rest } = piece;
                if (criteria) {
                    // if we have criteria, we need to merge it with the existing criteria, in a way that doesn't break logic (like merging 2 $ors)
                    if (filterObject.criteria) {
                        if (filterObject.criteria.$and) {
                            filterObject.criteria.$and.push(criteria);
                        } else {
                            filterObject.criteria = { $and: [filterObject.criteria, criteria] };
                        }
                    } else {
                        filterObject.criteria = criteria;
                    }
                }
                filterObject = merge(filterObject, rest);
            }
        }
    });
    Object.keys(filterObject).forEach(key => {
        filterPieces.push(`${key}=${encodeURIComponent(JSON.stringify(filterObject[key]))}`);
    });

    if (filterPieces.length > 0) {
        if (correlationId) {
            return `${baseUrl}?${filterPieces.join('&')}&correlationId=${encodeURIComponent(correlationId)}`;
        }
        return `${baseUrl}?${filterPieces.join('&')}`;
    }
    return baseUrl;
}
